import { inject, Injectable, Optional, ViewContainerRef } from '@angular/core';

import type { Observable } from 'rxjs';
import { filter, Subject, takeUntil } from 'rxjs';
import { map, take } from 'rxjs/operators';

import type { UserIdMap } from '@core/account-data';
import type { CurrentUser } from '@core/account-types';
import { DecisionStatus, RequisitionStatus } from '@core/requisitions-types';
import type {
  ApprovalStepWithDecisions,
  Requisition,
  RequisitionFlowStep,
} from '@core/requisitions-types';

import type { RequisitionsAbilities } from '../../data/single-requisition/requisitions-data-provider.service';
import { RequisitionsDataProviderService } from '../../data/single-requisition/requisitions-data-provider.service';
import { RequisitionsDialogsService } from '../../services/requisitions-dialogs.service';

@Injectable()
export class EditApproversFacade {
  private readonly vcRef = inject(ViewContainerRef);
  private currentUser: CurrentUser;
  private users: UserIdMap;
  private requisition: Requisition;
  private abilities: RequisitionsAbilities;

  private ngOnDestroy$ = new Subject<void>();
  constructor(
    @Optional()
    private requisitionsDialogsService: RequisitionsDialogsService,
    protected requisitionsData: RequisitionsDataProviderService,
  ) {
    requisitionsData.data$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ requisition, abilities, currentUser, users }) => {
        this.requisition = requisition;
        this.currentUser = currentUser;
        this.users = users;
        this.abilities = abilities;
      });
  }

  public selectApprovers$(
    step: ApprovalStepWithDecisions,
  ): Observable<number[] | undefined> | undefined {
    const assignedApprovers = [...step.approverIds];
    const existingApprovers = assignedApprovers.filter(id => !!this.users[id]);
    const approversWhoReviewed =
      step.approverDecisions
        ?.filter(
          decision =>
            decision.status === DecisionStatus.Approved ||
            decision.status === DecisionStatus.Rejected,
        )
        .map(decision => decision.approverId) || [];
    const existingApproversWhoReviewed = approversWhoReviewed.filter(id => !!this.users[id]);
    const deletedApproversWhoReviewed = approversWhoReviewed.filter(id => !this.users[id]);

    return this.requisitionsDialogsService
      ?.openApproversSelectDialog$(existingApprovers, existingApproversWhoReviewed, this.vcRef)
      .pipe(
        take(1),
        filter(selectedUsers => !!selectedUsers?.length),
        map(selectedUsers => {
          const newApprovers: number[] = [];
          // deleted approvers who gave the review must be added here to be sent to the backend
          selectedUsers.map(user => user.id).forEach(id => newApprovers.push(id));
          deletedApproversWhoReviewed.forEach(id => newApprovers.push(id));
          newApprovers.sort();

          if (
            newApprovers.length === assignedApprovers.length &&
            newApprovers.every((value, index) => assignedApprovers[index] === value)
          ) {
            return;
          }

          return newApprovers;
        }),
      );
  }

  public isStepEditable(step: RequisitionFlowStep): boolean {
    const allowedStatuses = [
      RequisitionStatus.Draft,
      RequisitionStatus.Pending,
      RequisitionStatus.Rejected,
    ];
    return (
      allowedStatuses.includes(this.requisition.status) &&
      (this.abilities.highLevelDataAccess ||
        (step.allowsEdit && step.approverIds.includes(this.currentUser.id)))
    );
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
